/**
 * Shared supervisor inspection write path.
 *
 * Both SupervisorInspectionDetailPage and the supervisor QC checklist view
 * persist verdicts through these helpers so there is exactly one code path:
 * linked inspection -> inspectionsService.complete, otherwise the task state
 * machine via tasksService.updateTaskStatus, with an offline queue fallback
 * through enqueueOfflineTaskPatch / enqueueOfflineDefect.
 */
import inspectionsService, { InspectionRow } from '../../services/api/inspectionsService';
import tasksService, { Task } from '../../services/api/tasksService';
import { enqueueOfflineDefect, enqueueOfflineTaskPatch } from '../../services/offline/fieldOfflineForms';
import { InspectionVerdict, verdictLifecycleStatus } from './supervisorFieldUtils';

export interface VerdictResult {
  queuedOffline: boolean;
  task: Task | null;
}

const isNetworkFailure = (err: any) => !err?.response || err?.code === 'ERR_NETWORK';

export async function submitInspectionVerdict(
  task: Task,
  linkedInspection: InspectionRow | null,
  verdict: InspectionVerdict,
  options: { resultSummary?: string | null; score?: number | null } = {}
): Promise<VerdictResult> {
  const lifecycleStatus = verdictLifecycleStatus(verdict);
  const queueOffline = async (): Promise<VerdictResult> => {
    const queued = await enqueueOfflineTaskPatch(
      task,
      { lifecycleStatus: lifecycleStatus.toLowerCase() },
      { lifecycleStatus, status: lifecycleStatus }
    );
    return { queuedOffline: true, task: queued };
  };

  if (!navigator.onLine) return queueOffline();

  try {
    const inspectionStatus = verdict === 'passed' ? 'passed' : 'failed';
    if (linkedInspection) {
      await inspectionsService.complete(linkedInspection.id, {
        status: inspectionStatus,
        resultSummary: options.resultSummary ?? null,
        score: options.score ?? (verdict === 'passed' ? 100 : verdict === 'conditional' ? 50 : 0),
      });
    } else {
      await tasksService.updateTaskStatus(task.id, lifecycleStatus);
    }
    return { queuedOffline: false, task: null };
  } catch (err) {
    if (isNetworkFailure(err)) return queueOffline();
    throw err;
  }
}

export async function recordInspectionFailDefect(
  task: Task,
  payload: { title: string; description?: string; severity?: 'low' | 'medium' | 'high' | 'critical' }
): Promise<{ queuedOffline: boolean }> {
  const queueOffline = async () => {
    await enqueueOfflineDefect(task, payload);
    return { queuedOffline: true };
  };

  if (!navigator.onLine) return queueOffline();

  try {
    await tasksService.addDefect(task.id, payload);
    return { queuedOffline: false };
  } catch (err) {
    if (isNetworkFailure(err)) return queueOffline();
    throw err;
  }
}
